import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import * as apiGet from 'api/ingredientCategory/GetIngredientCategoryIdV1';
import IngredientCategory from 'classes/StandingData/IngredientCategory/IngredientCategory';
import IngredientCategoryList from 'classes/StandingData/IngredientCategory/Search/IngredientCategoryList';
import IngredientCategoryLanguageInput from 'components/desktop/StandingData/IngredientCategory/LanguageInput/IngredientCategoryLanguageInput';
import BtnSave from 'components/desktop/_general/Button/BtnSave/BtnSave';
import LanguageSwitch from 'components/desktop/_general/LanguageSwitch/LanguageSwitch';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import SelectSimple, {
	SelectOption,
} from 'components/desktop/_general/Select/SelectSimple/SelectSimple';
import { NutriScoreCategory } from 'enums/nutriScoreCategory';
import clone from 'functions/clone';
import { getIdFromUrl, setIdToUrl } from 'functions/url';
import { ISelectItem } from 'interfaces/ISelectItem';
import { RootState } from 'reducers/rootReducer';

const IngredientCategoryDetailComponent = () => {
	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
	const [selectedCultureCode, setSelectedCultureCode] = useState<string>('');

	const [ingredientCategory, setIngredientCategory] = useState<IngredientCategory>(
		new IngredientCategory()
	);
	const [id, setId] = useState<string | null>(null);
	const [parentList, setParentList] = useState<IngredientCategoryList>(
		new IngredientCategoryList()
	);

	useEffect(() => {
		initialize();
		setSelectedCultureCode(reduxCultureCode);
	}, []);

	const initialize = async (): Promise<void> => {
		const id: string | null = getIdFromUrl();
		setId(id);
		await getParents();
		if (id !== null && id !== 'new') {
			getIngredientCategory(id);
		}
	};

	const getParents = async (): Promise<void> => {
		await parentList.search('', false);
		setParentList(clone(parentList));
	};

	const getIngredientCategory = async (id: string): Promise<void> => {
		setIsLoading(true);
		const response = await apiGet.callApi(id);
		response.do((x) => setIngredientCategory(new IngredientCategory(x)));
		setIsLoading(false);
	};

	const handleInputValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		ingredientCategory.nameTranslations[selectedCultureCode] = e.currentTarget.value;
		setIngredientCategory(clone(ingredientCategory));
	};

	const handleTranslate = async () => {
		setIsLoading(true);
		await ingredientCategory.callApiTranslation(reduxCultureCode, selectedCultureCode);
		setIngredientCategory(clone(ingredientCategory));
		setIsLoading(false);
	};

	const handleSelectLanguage = (_e: any, selectedLanguage: string) => {
		setSelectedCultureCode(selectedLanguage);
	};

	const handleSaveIngredientCategory = async (): Promise<void> => {
		setIsLoadingSave(true);
		try {
			if (id !== null && id !== 'new') {
				await ingredientCategory.callApiPut();
			} else {
				await ingredientCategory.callApiPost();
				if (!ingredientCategory.id) return;
				setId(ingredientCategory.id);
				setIdToUrl(ingredientCategory.id);
			}
		} catch {
			setIsLoadingSave(false);
		} finally {
			setIsLoadingSave(false);
		}
	};

	const handleChangeParent = (id: string) => {
		const parent = parentList.filtered.find((x: any) => x.id == id);
		if (parent) {
			ingredientCategory.parent = parent;
		} else {
			ingredientCategory.parent = null;
		}
		setIngredientCategory(clone(ingredientCategory));
	};

	const getNutriScoreCategories = (): JSX.Element => {
		return (
			<>
				<option key="-999" value="__default" disabled={true}>
					{t('_general:NO_NUTRI_SCORE_CATEGORY')}
				</option>
				{Object.values(NutriScoreCategory).map((s, i) => (
					<option key={i} value={s}>
						{t('nutriScoreCategory:' + s)}
					</option>
				))}
			</>
		);
	};

	const setNutriScoreCategory = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value: NutriScoreCategory = e.currentTarget.value as NutriScoreCategory;
		ingredientCategory.nutriScoreCategory = value;
		setIngredientCategory(clone(ingredientCategory));
	};

	const getParentOptions = (): SelectOption[] => {
		const output: SelectOption[] = [];
		output.push({ id: '', text: t('standingData:NOTHING_SELECTED') });
		parentList.filtered.forEach((group: ISelectItem) => {
			output.push({ id: group.id, text: group.name });
		});
		return output;
	};

	const renderContent = () => {
		return (
			<>
				<LoadingAnimation isLoading={isLoading} />
				<div className="row">
					<div className="d-flex flex-row justify-content-between mb-3">
						<div>
							{ingredientCategory.id ? (
								<>
									<h1>{t('standingData:EDIT_INGREDIENTCATEGORY')}</h1>
								</>
							) : (
								<>
									<h1>{t('standingData:NEW_INGREDIENTCATEGORY')}</h1>
								</>
							)}
						</div>
						<div>
							<BtnSave
								handleSave={() => handleSaveIngredientCategory()}
								isLoading={isLoadingSave}
							/>
						</div>
					</div>
				</div>
				<div className="row">
					<div className="col-md-4 col-xl-3 col-lg-3 col-sm-4 col-12">
						<label>{t('standingData:PARENT')}</label>
						<SelectSimple
							options={getParentOptions()}
							currentValue={
								ingredientCategory.parent ? ingredientCategory.parent.id : null
							}
							handleValueChange={handleChangeParent}
							errorKey="CategoryGroupId"
						/>
						<label>{t('_general:NUTRI_SCORE_CATEGORY')}</label>
						<select
							className="form-select"
							value={ingredientCategory.nutriScoreCategory ?? '__default'}
							name="nutriScoreSelect"
							onChange={setNutriScoreCategory}
							error-key="NutriScoreCategory"
						>
							{getNutriScoreCategories()}
						</select>
					</div>
					<div className="col-mg-8 col-xl-9 col-sm-8 col-12">
						<LanguageSwitch
							handleSelectLanguage={handleSelectLanguage}
							selectedLanguage={selectedCultureCode}
							translatable={true}
							onTranslateClick={handleTranslate}
							entryLanguage={reduxCultureCode}
							content={
								<IngredientCategoryLanguageInput
									ingredientCategory={ingredientCategory}
									selectedLanguage={selectedCultureCode}
									handleValueChange={handleInputValueChange}
								/>
							}
						/>
					</div>
				</div>
			</>
		);
	};

	return <>{renderContent()}</>;
};

export default IngredientCategoryDetailComponent;
